// Per-puzzle TRACE for the rebus game — one create-only Firestore doc per attempt, written
// as each puzzle finishes. Shares the capture stream types; `domain: 'rebus'` distinguishes it
// in the single `labsTraces` collection (BQ `traces_raw_latest` filters on it).

import { TranscriptItem, InteractionEvent, CaptureMode } from '../../../shared/labs/types'
import { CaptureEnv } from '../../../shared/lib/env'

export type RebusAnswerMode = 'typed' | 'voice-detected' | 'pass' | 'timeout' | null

export type RebusTrace = {
  schemaVersion: number
  domain: 'rebus'
  // grouping / session
  sessionId: string
  mode: CaptureMode
  trialIndex: number
  sitting: number
  resumed: boolean
  aborted: boolean
  // puzzle identity + difficulty
  puzzleId: string // e.g. "10_3"
  imageUrl: string
  answer: string // canonical answer (offline scoring; never shown pre-commit)
  llmSolveRate: number // mean LLM correctness (0..1) — difficulty axis + human-vs-LLM anchor
  band: number | null // test-mode difficulty band; null in free play
  // the trace itself
  transcript: TranscriptItem[]
  events: InteractionEvent[]
  committedTranscript: string
  sttEngine: 'webspeech' | 'deepgram' | 'google'
  // answer + correctness
  answerMode: RebusAnswerMode
  typedAnswer: string | null
  matchedAnswer: string | null
  correct: boolean
  correctnessReason: string
  // timing
  puzzleStartedAt: string // ISO wall clock
  puzzleDurationMs: number
  reflectionDurationMs: number // commit → answer entry (covers the retro when one was shown)
  // instant-solve retrospective (aha payoff) — only populated when the solve was trivial
  // (fast / near-wordless; see trivialSolve.ts), which swaps the passive reflection for an active
  // untimed retro. The retro's spoken decode lives IN `transcript` in-stream (like the reflection),
  // bracketed by the retro_start/retro_end events; these fields carry what the transcript can't.
  trivialSolve: boolean
  solutionType: 'insight' | 'analytic' | null // self-reported: did it click, or was it worked out?
  // environment + consent
  env: CaptureEnv
  consented: boolean
  source: { src: string | null; token: string | null }
  firstExposure: boolean
}

export const REBUS_TRACE_SCHEMA_VERSION = 2 // v2: instant-solve retro fields
